import { useBuilding, snackMenuItemsOptions, createSnackMenuItem } from "@/api";
import PageBackButton from "@/components/PageBackButton";
import { useBuildingQuery } from "@/components/RouterSetup";
import MenuItemForm from "@/components/snack/MenuItemForm";
import { useWelcomeSnackAccess } from "@/hooks/useWelcomeSnackAccess";
import { getBuildingId, notifyApiError } from "@/lib";
import { useSnackbarStore } from "@/store/zustandStore";
import { SnackMenuItem, NotificationType } from "@/types";
import { Breadcrumbs } from "@app-components";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_portal/snack-admin/menu/item/new")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const { allowSnackAdminAccess } = useWelcomeSnackAccess();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addNotification } = useSnackbarStore();
  const { data: building } = useBuilding();
  const { data: menu } = useBuildingQuery(
    snackMenuItemsOptions(building.supplierCompanyId, true),
  );

  const breadcrumbs = [
    { label: t("SIDEBAR.SNACK_MENU"), path: "/snack-admin/menu" },
    {
      label: t("SIDEBAR.SNACK_NEW_ITEM"),
      path: `/snack-admin/menu/item/new`,
    },
  ];

  const handleSubmit = async (
    values: Partial<SnackMenuItem>,
    onSuccess: () => void,
  ) => {
    const entity =
      values.name?.[i18n.language === "en" ? "en" : "no"] ??
      t("SIDEBAR.SNACK_NEW_ITEM").toLowerCase();
    try {
      if (!menu) {
        addNotification(t("NOTIFICATIONS.MISSING_OR_INVALID_PARAMS"));
        return;
      }

      await createSnackMenuItem(menu.id, values);

      addNotification(
        t("NOTIFICATIONS.DOCUMENT_CREATED", {
          entity,
        }),
        NotificationType.SUCCESS,
      );

      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "menu-items", building.supplierCompanyId],
      });

      onSuccess();
      setTimeout(() => {
        navigate({ to: `/snack-admin/menu` });
      }, 1);
    } catch (error) {
      console.error(error);
      notifyApiError(error, addNotification, t, {
        properties: {
          entity,
        },
        fallbackKey: "CREATE_FAILED",
      });
    }
  };

  if (!allowSnackAdminAccess) return null;
  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton fallbackPath={{ to: breadcrumbs[0].path }} />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <MenuItemForm handleSubmit={handleSubmit} />
    </>
  );
}
